import { useEffect, useRef, useState, useMemo } from "react";
import {
  Grid,
  Typography,
  Button,
  Box,
  Snackbar,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Divider,
  InputAdornment,
  Stack,
} from "@mui/material";
import Layout from "../../layout/Layout";
import {
  fetchCustomerAPI,
  fetchUnitsAPI,
  fetchStatusByParent,
  updateCustomerByIdAPI,
} from "../../config";
import { useNavigate, useLocation } from "react-router-dom";
import { usePersistedState } from "../../utils/usePersistedState";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import { MaterialReactTable } from "material-react-table";
import { apiData } from "../../services/api_service";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import { FileDownload } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { fitColumns } from "../../utils/excelFit";
import moment from "moment";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CloseIcon from "@mui/icons-material/Close";
import { writeXlsxFrozen } from "../../utils/xlsxFreeze";

function CustomersList() {
  const user = JSON.parse(localStorage.getItem("user"));
  // role_id isn't a fixed "Admin" constant across environments — position_name
  // is the reliable check. Admin and Chief Executive both get the same access.
  const SUPER_ADMIN_POSITIONS = ["admin", "chief executive"];
  const isAdmin = SUPER_ADMIN_POSITIONS.includes(
    (user?.position_name || "").trim().toLowerCase()
  );
  const navigate = useNavigate();
  const location = useLocation();

  const [totalCount, setTotalCount] = useState(0);
  const [customerList, setCustomerList] = useState([]);
  const [status, setStatus] = useState([]);

  const handleEditStore = (id) => {
    navigate(`/customer/edit/${id}`);
  };

  const handleOpenCreditDaysDialog = (customer) => {
    setEditingCustomer(customer);
    const originalValue =
      customer.credit_days === null || customer.credit_days === undefined
        ? ""
        : customer.credit_days;
    setOriginalCreditDays(originalValue);
    setCreditDaysValue(originalValue);
    setDialogOpen(true);
  };

  const handleCloseCreditDaysDialog = () => {
    setDialogOpen(false);
    setEditingCustomer(null);
    setCreditDaysValue("");
    setOriginalCreditDays("");
  };

  const handleSaveCreditDays = async () => {
    try {
      setSavingCredit(true);
      const payload = {
        credit_days: creditDaysValue === "" ? null : Number(creditDaysValue),
      };

      const result = await apiData(
        "put",
        `${updateCustomerByIdAPI}/${editingCustomer.customer_id}`,
        payload
      );

      if (result.success) {
        setSuccessMessage("Credit days updated successfully!");
        handleCloseCreditDaysDialog();
        fetchUsers(); // Refresh the list
      } else {
        setSuccessMessage(result.msg || "Failed to update credit days");
      }
    } catch (error) {
      setSuccessMessage(error.msg || "Error updating credit days");
    } finally {
      setSavingCredit(false);
    }
  };

  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [creditDaysValue, setCreditDaysValue] = useState("");
  const [originalCreditDays, setOriginalCreditDays] = useState("");
  const [savingCredit, setSavingCredit] = useState(false);

  const [pageIndex, setPageIndex] = usePersistedState("customer-list:pageIndex", 0);
  const [pageSize, setPageSize] = usePersistedState("customer-list:pageSize", 100);
  const [filters, setFilters] = usePersistedState("customer-list:filters", []);
  // Dashboard drill-down (Active / New Customers card): "ordered" = customers
  // with an invoice in the period, "created" = customers created in the period.
  const [dashScope, setDashScope] = usePersistedState("customer-list:dashScope", () => {
    const st = location.state || {};
    if (st.customerScope !== "ordered" && st.customerScope !== "created") return null;
    return {
      scope: st.customerScope,
      from: st.fromDate || "",
      to: st.toDate || "",
      storeId: Number(st.storeId) > 0 ? Number(st.storeId) : 0,
      createdBy: Number(st.salesPersonId) > 0 ? Number(st.salesPersonId) : 0,
    };
  });
  const scopeParams = () =>
    !dashScope
      ? {}
      : dashScope.scope === "ordered"
        ? {
            ordered: 1,
            ordered_from: dashScope.from || undefined,
            ordered_to: dashScope.to || undefined,
            store_id: dashScope.storeId || undefined,
            created_by: dashScope.createdBy || undefined,
          }
        : { created_from: dashScope.from || undefined, created_to: dashScope.to || undefined };
  const [sorting, setSorting] = usePersistedState("customer-list:sorting", []);
  const [successMessage, setSuccessMessage] = useState("");

  let tempRefs = useRef(false);
  useEffect(() => {
    if (tempRefs.current === false) {
      fetchUsers(1);
    }
  }, []);

  const fetchUsers = async () => {
    const payload = {
      pageIndex: pageIndex,
      pageSize: pageSize,
      sorting: sorting.length > 0 ? JSON.stringify(sorting[0]) : null,
      search: filters.length > 0 ? JSON.stringify(filters) : null,
      ...scopeParams(),
    };
    try {
      const response = await apiData("get", fetchCustomerAPI, payload);
      setCustomerList(response.data.result);
      setTotalCount(response.data.totalCount);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [pageIndex, pageSize, filters, sorting, dashScope]);

  useEffect(() => {
    const fetchStatus = async () => {
      const data = {
        intParentId: 1,
      };

      try {
        const result = await apiData("get", fetchStatusByParent, data);
        if (result.success) {
          setStatus(result.data);
        }
      } catch (error) {
        console.error("Failed to fetch units:", error);
      }
    };

    fetchStatus();
  }, []);

  const handleExportData = async () => {
    try {
      setLoading(true);

      // Prepare payload with all filters, sorting, search, but unlimited pageSize
      const payload = {
        pageIndex: 0,
        pageSize: 1000000, // effectively "all" records
        sorting: sorting.length > 0 ? JSON.stringify(sorting[0]) : null,
        search: filters.length > 0 ? JSON.stringify(filters) : null,
        ...scopeParams(),
      };

      const response = await apiData("get", fetchCustomerAPI, payload);

      if (!response?.data?.result || response.data.result.length === 0) {
        alert("No data to export!");
        return;
      }

      const exportData = response.data.result.map((item, index) => ({
        "#": index + 1,
        "Customer Type": item.customer_type || "Company",
        "Company Name": item.company_name || "",
        "Customer / Contact Name": item.name || "",
        Phone: item.phone_number || "",
        Email: item.email || "",
        Address: item.address || "",
        Emirate: item.emirate || "",
        "PO Box": item.po_box || "",
        "VAT No.": item.vat_no || "",
        "Credit Days": item.credit_days ?? "",
        Status:
          item.status === 2 ? "Active" : item.status === 3 ? "InActive" : "",
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      fitColumns(ws);
      XLSX.utils.book_append_sheet(wb, ws, "customer_list");

      const fileName = `customer_list_${moment().format(
        "DD_MM_YYYY"
      )}.xlsx`;
      writeXlsxFrozen(XLSX, wb, fileName);
      setSuccessMessage("Excel Downloaded Successfully!");
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export data.");
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => [
    {
      accessorKey: "customer_type",
      header: "Type",
      size: 90,
      minSize: 80,
      Cell: ({ cell }) => {
        const val = cell.getValue() || "Company";
        const isCompany = val === "Company";
        return (
          <Chip
            label={val}
            size="small"
            color={isCompany ? "primary" : "secondary"}
            variant="outlined"
            sx={{ fontSize: "0.7rem", height: 22 }}
          />
        );
      },
    },
    {
      accessorKey: "name",
      header: "Customer / Company Name",
      size: 170,
      minSize: 140,
      Cell: ({ row }) => {
        const company = row.original.company_name;
        const name = row.original.name;

        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            {company ? (
              <>
                <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                  {company}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Contact: {name}
                </Typography>
              </>
            ) : (
              <Typography variant="subtitle2" fontWeight={600}>
                {name}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      accessorKey: "phone_number",
      header: "Phone",
      size: 85,
      minSize: 80,
      Cell: ({ cell }) => (
        <Tooltip title={cell.getValue() || "-"} arrow>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
            {cell.getValue() || "-"}
          </span>
        </Tooltip>
      ),
    },
    {
      accessorKey: "email",
      header: "Email",
      size: 140,
      minSize: 130,
      Cell: ({ cell }) => (
        <Tooltip title={cell.getValue() || "-"} arrow>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
            {cell.getValue() || "-"}
          </span>
        </Tooltip>
      ),
    },
    {
      accessorKey: "address",
      header: "Address",
      size: 150,
      minSize: 140,
      Cell: ({ cell }) => (
        <Tooltip title={cell.getValue() || "-"} arrow>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
            {cell.getValue() || "-"}
          </span>
        </Tooltip>
      ),
    },

    {
      accessorKey: "emirate",
      header: "Emirate",
      size: 85,
      minSize: 80,
    },
    {
      accessorKey: "po_box",
      header: "PO Box",
      size: 100,
      minSize: 90,
    },
    {
      // Customer form/PDFs collect "VAT No." (vat_no), not "TRN"
      // (trn_number) — that field's been retired everywhere else (see
      // [[trn-to-vat-no-rename]]); this column was still showing the old,
      // no-longer-collected trn_number.
      accessorKey: "vat_no",
      header: "VAT No.",
      size: 120,
      minSize: 110,
      Cell: ({ cell }) => (
        <Tooltip title={cell.getValue() || "-"} arrow>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
            {cell.getValue() || "-"}
          </span>
        </Tooltip>
      ),
    },
    // Global per-customer Credit Days column removed (commented out, not
    // deleted) — credit terms are now only ever granted through the
    // per-user request/approval flow (tbl_credit_approval_requests); the
    // backend no longer honors tbl_customers.credit_days at all for invoice
    // creation, so showing/editing it here would be misleading.
    // {
    //   accessorKey: "credit_days",
    //   header: "Credit Days",
    //   size: 130,
    //   minSize: 120,
    //   Cell: ({ row }) => {
    //     const creditDays = row.original.credit_days;
    //     const isConfigured = creditDays !== null && creditDays !== undefined;
    //
    //     const badge = isConfigured ? (
    //       <Chip
    //         size="small"
    //         label={`${creditDays} ${Number(creditDays) === 1 ? "day" : "days"}`}
    //         sx={{
    //           fontWeight: 700,
    //           fontSize: 12.5,
    //           height: 26,
    //           color: "#0f5132",
    //           backgroundColor: "#e7f6ec",
    //           border: "1px solid #b7e4c7",
    //         }}
    //       />
    //     ) : (
    //       <Chip
    //         size="small"
    //         variant="outlined"
    //         label="Not configured"
    //         sx={{
    //           fontWeight: 600,
    //           fontSize: 12.5,
    //           height: 26,
    //           color: "text.disabled",
    //           borderStyle: "dashed",
    //           borderColor: "#d0d5dd",
    //         }}
    //       />
    //     );
    //     return badge;
    //   },
    //   enableSorting: true,
    //   enableColumnFilter: false,
    // },
    {
      accessorKey: "status",
      header: "Status",
      size: 110,
      minSize: 100,
      Cell: ({ row }) => {
        const name = status?.find(
          (option) => option.id === row.original.status
        )?.name;
        return (
          <span
            style={{
              color: row.original.status === 2 ? "green" : "red",
              fontWeight: 600,
            }}
          >
            {name || "N/A"}
          </span>
        );
      },
      filterVariant: "select",
      filterSelectOptions: [
        {
          label: "Active",
          title: "Active",
          value: 2,
        },
        {
          label: "InActive",
          title: "InActive",
          value: 3,
        },
      ],
    },
    {
      accessorKey: "edit",
      header: "Action",
      size: 90,
      minSize: 85,
      Cell: ({ row }) => (
        <IconButton
          color="primary"
          onClick={() => handleEditStore(row.original.customer_id)}
          sx={{ p: 0.5 }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      ),
      enableSorting: false,
      enableColumnFilter: false,
    },
  ]);

  return (
    <Layout>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000} // hide after 3 seconds
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSuccessMessage("")}
          severity="success"
          sx={{
            width: "100%",
            backgroundColor: "#28a745",
            color: "#fff",
            fontWeight: 600,
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Typography className="page_title" textAlign="left">
              {location.state?.reportTitle || location.state?.title || "Manage Vendors"}
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Vendors</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid
            textAlign="right"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <LoadingButton
              size="medium"
              variant="contained"
              color="success"
              className="button_first"
              href="add"
              startIcon={<AddIcon />}
            >
              Add Vendor
            </LoadingButton>
            <LoadingButton
              size="medium"
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </LoadingButton>
          </Grid>
        </Grid>

        {dashScope && (
          <Alert
            severity="info"
            sx={{ mb: 1.5 }}
            action={
              <Button color="inherit" size="small" onClick={() => { setPageIndex(0); setDashScope(null); }}>
                Clear filter
              </Button>
            }
          >
            Filtered from the dashboard —{" "}
            {dashScope.scope === "ordered" ? "customers who placed an invoice" : "customers created"}
            {dashScope.from || dashScope.to
              ? ` between ${dashScope.from || "start"} and ${dashScope.to || "today"}`
              : " (all time)"}
            . Showing {totalCount} customers.
          </Alert>
        )}

        <Grid className="form_design">
          <MaterialReactTable
            columns={columns}
            data={customerList}
            enableGlobalFilter={false}
            enableFullScreenToggle={false}
            className="mat-react-tbl"
            layoutMode="semantic"
            enableStickyHeader
            initialState={{
              columnPinning: {
                right: ["status", "edit"],
              },
            }}
            state={{
              isLoading: loading,
              pagination: { pageIndex, pageSize },
              sorting,
              columnFilters: filters,
            }}
            manualPagination
            enableRowNumbers
            manualSorting
            manualFiltering
            rowCount={totalCount}
            onPaginationChange={(updater) => {
              const paginationState = updater({
                pageIndex,
                pageSize,
              });
              const { pageIndex: newPageIndex, pageSize: newPageSize } =
                paginationState;
              setPageIndex(newPageIndex);
              setPageSize(newPageSize);
            }}
            onSortingChange={setSorting}
            onColumnFiltersChange={setFilters}
            muiTableContainerProps={{
              sx: {
                maxHeight: "calc(100vh - 240px)",
                minHeight: 450,
                overflowX: "auto",
                overflowY: "auto",
                // The previous rule here targeted ".Mui-TableHeadCell", which
                // isn't a real MUI/MRT class — it never matched anything, so
                // the header row had no forced sticky styling of its own and
                // relied entirely on enableStickyHeader's internal (lower-
                // specificity) style, which the pinned-column CSS below could
                // win a stacking fight against. Force it directly, with
                // !important so it can't lose that fight.
                "& .MuiTableCell-head": {
                  position: "sticky !important",
                  top: "0 !important",
                  zIndex: "20 !important",
                },
              },
            }}
            muiTableBodyCellProps={{
              sx: {
                padding: "8px 12px",
                fontSize: "0.875rem",
                border: "1px solid #e0e0e0",
                // Pinned body cells (Status/Action) get their sticky
                // right-edge styling via data-pinned, not the
                // ".MuiTableCell-stickyHeader" class (that's a head-cell-only
                // class — it never matched here on body cells).
                '&[data-pinned="true"]': {
                  backgroundColor: "#fafafa",
                  boxShadow: "-2px 0 4px rgba(0, 0, 0, 0.1)",
                },
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                padding: "10px 12px",
                border: "1px solid #1565c0",
                color: "#fff !important",
                fontWeight: "700",
                fontSize: "0.875rem",
                backgroundColor: "#1976d2",
                whiteSpace: "nowrap",
                // Pinned header cells (Status/Action) — use data-pinned, not
                // ".MuiTableCell-stickyHeader" (that class means "this is a
                // head cell under enableStickyHeader", not "this column is
                // pinned"; it's on every header cell, not just pinned ones).
                // Also don't hardcode right:0 here — MRT already sets each
                // pinned column's own true right offset inline so Status and
                // Action stack correctly instead of overlapping at 0.
                '&[data-pinned="true"]': {
                  backgroundColor: "#1565c0",
                  zIndex: "21 !important",
                  boxShadow: "-2px 0 4px rgba(0, 0, 0, 0.15)",
                },
                "& svg": {
                  color: "#fff !important",
                },
                "& .MuiTableSortLabel-root": {
                  color: "#fff !important",
                  "&.Mui-active": {
                    color: "#fff !important",
                    "& .MuiTableSortLabel-icon": {
                      color: "#fff !important",
                    },
                  },
                },
                "& .MuiTableSortLabel-icon": {
                  color: "#fff !important",
                },
                "& .MuiInputBase-input": {
                  color: "#fff !important",
                  textFillColor: "#fff !important",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#fff !important",
                  opacity: 0.8,
                },
                "& .MuiInput-underline:before": {
                  borderBottomColor: "#fff !important",
                },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottomColor: "#fff !important",
                },
                "& .MuiInput-underline:after": {
                  borderBottomColor: "#fff !important",
                },
              },
            }}
            muiTableBodyRowProps={({ row }) => ({
              sx: {
                borderBottom: "1px solid #ccc",
                backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
              },
            })}
            muiTableHeadCellFilterTextFieldProps={{
              sx: {
                "& input": {
                  color: "#fff !important", // Change text color
                },
                "& .MuiInput-underline:before": {
                  borderBottomColor: "#fff !important", // Change underline color
                },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottomColor: "#fff !important", // Hover underline color
                },
                "& .MuiInput-underline:after": {
                  borderBottomColor: "#fff !important", // Focused underline color
                },
              },
            }}
            renderTopToolbarCustomActions={({ table }) => (
              <>
                <Box
                  sx={{
                    display: "flex",
                    gap: "1rem",
                    p: "0.5rem",
                    justifyContent: "space-between",
                    flexGrow: 1,

                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {/* RIGHT SIDE BUTTON */}
                    <Tooltip title="Export data to excel sheet" arrow>
                      <Button
                        onClick={handleExportData}
                        style={{ background: "#1a73e829" }}
                        startIcon={<FileDownload />}
                        // startIcon={<IconTableExport />}
                      >
                        Export To Excel
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
              </>
            )}
          />
        </Grid>

        {/* Credit Days Edit Dialog — direct admin edit deprecated in favor of
            the request/approval flow; kept but force-closed for now. */}
        <Dialog
          open={false && dialogOpen}
          onClose={handleCloseCreditDaysDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2.5 } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#eef2ff",
                  color: "#2F5FED",
                }}
              >
                <EventAvailableIcon fontSize="medium" />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>
                  Credit Days
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {editingCustomer?.name}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={handleCloseCreditDaysDialog}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <Divider />

          <DialogContent sx={{ pt: 3, pb: 2 }}>
            <TextField
              autoFocus
              fullWidth
              type="number"
              label="Credit Days"
              size="medium"
              inputProps={{ min: 0 }}
              value={creditDaysValue}
              onChange={(e) => setCreditDaysValue(e.target.value)}
              onFocus={(e) => e.target.select()}
              placeholder="e.g. 30"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">days</InputAdornment>
                ),
              }}
              helperText={
                creditDaysValue !== "" &&
                (isNaN(Number(creditDaysValue)) || Number(creditDaysValue) < 0)
                  ? "Enter a valid, non-negative number of days"
                  : "Leave blank to remove credit terms for this customer"
              }
              error={
                creditDaysValue !== "" &&
                (isNaN(Number(creditDaysValue)) || Number(creditDaysValue) < 0)
              }
            />

            <Typography
              variant="body2"
              sx={{ display: "block", mt: 3, mb: 2, color: "text.secondary", fontWeight: 700 }}
            >
              QUICK SELECT
            </Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              {[7, 15, 30, 45, 60, 90].map((preset) => (
                <Chip
                  key={preset}
                  label={`${preset}d`}
                  size="medium"
                  clickable
                  onClick={() => setCreditDaysValue(String(preset))}
                  variant={
                    String(preset) === String(creditDaysValue) ? "filled" : "outlined"
                  }
                  color={
                    String(preset) === String(creditDaysValue) ? "primary" : "default"
                  }
                  sx={{ fontWeight: 600, fontSize: "0.95rem" }}
                />
              ))}
              <Chip
                label="Clear"
                size="medium"
                clickable
                onClick={() => setCreditDaysValue("")}
                variant={creditDaysValue === "" ? "filled" : "outlined"}
                sx={{ fontWeight: 600, fontSize: "0.95rem" }}
              />
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, gap: 1 }}>
            <Button 
              onClick={handleCloseCreditDaysDialog} 
              color="inherit"
              size="large"
            >
              Cancel
            </Button>
            <Tooltip
              title={
                creditDaysValue === String(originalCreditDays)
                  ? "No changes made yet"
                  : ""
              }
            >
              <span>
                <LoadingButton
                  onClick={handleSaveCreditDays}
                  variant="contained"
                  loading={savingCredit}
                  disabled={
                    creditDaysValue === String(originalCreditDays) ||
                    (creditDaysValue !== "" &&
                      (isNaN(Number(creditDaysValue)) || Number(creditDaysValue) < 0))
                  }
                  size="large"
                  sx={{ minWidth: 110 }}
                >
                  Save
                </LoadingButton>
              </span>
            </Tooltip>
          </DialogActions>
        </Dialog>
      </Grid>
    </Layout>
  );
}

export default CustomersList;
